import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AccountBalance,
  AddCircle,
  AttachFile,
  CheckCircle,
  Download,
  Edit,
  Delete,
  Block,
  History,
  InsertDriveFile,
  Payments,
  Refresh,
  ReceiptLong,
  Search,
  Visibility,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch } from 'react-redux';
import MainText from '../../components/MainText';
import CustomSnackBar from '../../components/CustomSnackBar';
import API from '../../services/api';
import { addTab } from '../../tabs/tabsSlice';
import { extractApiError } from '../../utils/apiError';

const BANK_REQUISITION_FLOW = 'BANK_REQUISITION';

const emptyObligationForm = {
  provider_id: '',
  purchase_order_id: '',
  bank_requisition_id: '',
  dispatch_number: '',
  requisition_number: '',
  dispatch_date: '',
  initial_amount: '',
  notes: '',
  invoice_ids: [],
};

const emptyPaymentForm = {
  payment_date: new Date().toISOString().slice(0, 10),
  capital_amount: '',
  interest_amount: '0',
  late_fee_amount: '0',
  reference: '',
  notes: '',
};

const emptyCreditItemForm = {
  invoice_id: '',
  item_type: 'EXPECTED',
  amount: '',
  number: '',
  date: new Date().toISOString().slice(0, 10),
  reason: '',
  expected_note_number: '',
  notes: '',
  correction_reason: '',
  file: null,
};

const statusOptions = [
  { value: 'PENDING', label: 'Pendiente' },
  { value: 'PARTIALLY_PAID', label: 'Parcial' },
  { value: 'PAID', label: 'Pagada' },
  { value: 'OVERDUE', label: 'Vencida' },
  { value: 'CANCELLED', label: 'Anulada' },
];

const statusLabels = statusOptions.reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {});

const statusColors = {
  PENDING: 'warning',
  PARTIALLY_PAID: 'info',
  PAID: 'success',
  OVERDUE: 'error',
  CANCELLED: 'default',
};

const bankPaymentStatusLabels = {
  PENDING_REQUISITION: 'Pendiente Abasto',
  PENDING_BANK_CAPTURE: 'Pendiente captura bancaria',
  CAPTURED: 'Captada',
  PARTIALLY_PAID_BANK: 'Parcial',
  PAID_BANK: 'Liquidada via banco',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-GT', {
    style: 'currency',
    currency: 'GTQ',
    minimumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return `${text.slice(8, 10)}/${text.slice(5, 7)}/${text.slice(0, 4)}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('es-GT');
};

const toNullable = (value) => {
  if (value === '' || value === undefined) return null;
  return value;
};

const toMoney = (value, fallback = '0') => {
  if (value === '' || value === undefined || value === null) return fallback;
  return value;
};

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const ALERT_WINDOW_DAYS = 7;

const parseLocalDate = (value) => {
  if (!value) return null;
  const text = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return new Date(Number(text.slice(0, 4)), Number(text.slice(5, 7)) - 1, Number(text.slice(8, 10)));
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const startOfLocalDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const daysUntil = (value) => {
  const date = parseLocalDate(value);
  if (!date) return null;
  const today = startOfLocalDay(new Date());
  const target = startOfLocalDay(date);
  return Math.ceil((target.getTime() - today.getTime()) / 86400000);
};

const isCurrentMonthDate = (value) => {
  const date = parseLocalDate(value);
  if (!date) return false;
  const today = new Date();
  return date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth();
};

const addDaysToDate = (value, days = 0) => {
  const date = parseLocalDate(value);
  if (!date) return '';
  date.setDate(date.getDate() + Number(days || 0));
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const canRegisterPayment = (obligation) => !['PAID', 'CANCELLED'].includes(String(obligation?.internal_status || '').toUpperCase());

const getOperationalAlerts = (item) => {
  const status = String(item?.internal_status || '').toUpperCase();
  const balance = toNumber(item?.balance);
  const interestLateFee = toNumber(item?.interest) + toNumber(item?.late_fee);
  const graceDays = daysUntil(item?.grace_end_date);
  const alerts = [];

  if (status === 'CANCELLED') {
    return [{ key: 'cancelled', label: 'Anulada', color: 'default' }];
  }

  if (status === 'PAID' || balance <= 0) {
    return [{ key: 'paid', label: 'Pagado', color: 'success' }];
  }

  if (status === 'PARTIALLY_PAID') {
    alerts.push({ key: 'partial', label: 'Parcial', color: 'info' });
  }

  if (status === 'OVERDUE' || (balance > 0 && graceDays !== null && graceDays < 0)) {
    alerts.push({ key: 'overdue', label: 'Vencido', color: 'error' });
  } else if (balance > 0 && graceDays !== null && graceDays <= ALERT_WINDOW_DAYS) {
    alerts.push({ key: 'soon', label: 'Proximo a vencer', color: 'warning' });
  }

  if (balance > 0) {
    alerts.push({ key: 'balance', label: 'Saldo > 0', color: 'warning' });
  }

  if (interestLateFee > 0) {
    alerts.push({ key: 'interest', label: 'Interes/mora', color: 'error' });
  }

  return alerts.length ? alerts : [{ key: 'pending', label: 'Pendiente', color: 'warning' }];
};

const csvCell = (value) => {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replace(/"/g, '""')}"`;
};

function StatusChip({ value }) {
  return (
    <Chip
      size="small"
      label={statusLabels[value] || value || 'Pendiente'}
      color={statusColors[value] || 'default'}
      variant={value === 'PAID' ? 'filled' : 'outlined'}
    />
  );
}

function AlertChips({ row }) {
  const alerts = getOperationalAlerts(row);
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
      {alerts.map((alert) => (
        <Chip
          key={alert.key}
          size="small"
          label={alert.label}
          color={alert.color}
          variant={alert.key === 'paid' || alert.key === 'overdue' ? 'filled' : 'outlined'}
          sx={{ maxWidth: 138 }}
        />
      ))}
    </Stack>
  );
}

function InvoiceChips({ row }) {
  const invoices = (
    row.linked_invoice_numbers?.length
      ? row.linked_invoice_numbers
      : (row.invoices || []).map((invoice) => `${invoice.invoice_series || ''}-${invoice.invoice_number || invoice.invoice_id}`.replace(/^-/, ''))
  ).filter(Boolean);

  if (!invoices.length) {
    return <Typography variant="caption" color="text.secondary">Sin facturas</Typography>;
  }

  const visible = invoices.slice(0, 2);
  const hidden = invoices.length - visible.length;
  return (
    <Tooltip title={invoices.join(', ')}>
      <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" alignItems="center">
        {visible.map((invoice) => (
          <Chip key={invoice} size="small" label={invoice} variant="outlined" />
        ))}
        {hidden > 0 && <Chip size="small" label={`+${hidden}`} variant="outlined" />}
      </Stack>
    </Tooltip>
  );
}

function DetailField({ label, value, strong = false }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 500 }}>
        {value === null || value === undefined || value === '' ? '-' : value}
      </Typography>
    </Box>
  );
}

function ObligationDialog({
  open,
  mode,
  form,
  setForm,
  suppliers,
  bankRequisitions,
  eligibleInvoices,
  loadingOptions,
  onManageCredits,
  onAddExpectedCredit,
  onAddRealCreditNote,
  onClose,
  onSave,
  saving,
}) {
  const supplierMap = useMemo(() => new Map(suppliers.map((supplier) => [Number(supplier.id), supplier])), [suppliers]);
  const invoiceMap = useMemo(() => new Map(eligibleInvoices.map((invoice) => [Number(invoice.id), invoice])), [eligibleInvoices]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleProviderChange = (event) => {
    setForm((prev) => ({
      ...prev,
      provider_id: event.target.value,
      purchase_order_id: '',
      bank_requisition_id: '',
      invoice_ids: [],
    }));
  };

  const handleBankRequisitionChange = (event) => {
    const value = event.target.value;
    const selected = bankRequisitions.find((item) => String(item.id) === String(value));
    setForm((prev) => ({
      ...prev,
      bank_requisition_id: value,
      purchase_order_id: selected?.purchase_order || '',
      requisition_number: selected?.requisition_number || '',
      initial_amount: '',
      invoice_ids: [],
    }));
  };

  const selectedSupplier = supplierMap.get(Number(form.provider_id));
  const selectedInvoices = useMemo(
    () => form.invoice_ids.map((id) => invoiceMap.get(Number(id))).filter(Boolean),
    [form.invoice_ids, invoiceMap]
  );
  const selectedTotal = useMemo(
    () => selectedInvoices.reduce((sum, invoice) => sum + toNumber(invoice.abasto_net_amount ?? invoice.available_balance ?? invoice.total), 0),
    [selectedInvoices]
  );
  const selectedRequisition = useMemo(
    () => bankRequisitions.find((item) => String(item.id) === String(form.bank_requisition_id)) || null,
    [bankRequisitions, form.bank_requisition_id]
  );
  const graceEndDate = useMemo(
    () => addDaysToDate(form.dispatch_date, selectedSupplier?.creditPeriod || 0),
    [form.dispatch_date, selectedSupplier?.creditPeriod]
  );
  const amountDifference = selectedTotal - toNumber(form.initial_amount);
  const orderAvailable = toNumber(selectedRequisition?.available_balance);
  const orderRemaining = Math.max(orderAvailable - selectedTotal, 0);
  const hasOrderSelected = Boolean(form.purchase_order_id || form.bank_requisition_id);

  const invoiceLabel = (invoice) => `${invoice.series || ''}-${invoice.number || invoice.id}`.replace(/^-/, '');

  const toggleInvoice = (invoiceId) => {
    const candidate = invoiceMap.get(Number(invoiceId));
    const canSelect = candidate?.can_select_for_obligation
      ?? toNumber(candidate?.available_balance) > 0;
    if (!canSelect) return;
    setForm((prev) => {
      const current = prev.invoice_ids.map(Number);
      const id = Number(invoiceId);
      const exists = current.includes(id);
      const nextIds = exists ? current.filter((item) => item !== id) : [...current, id];
      const nextTotal = nextIds.reduce((sum, item) => {
        const invoice = invoiceMap.get(Number(item));
        return sum + toNumber(invoice?.abasto_net_amount ?? invoice?.available_balance ?? invoice?.total);
      }, 0);
      return {
        ...prev,
        invoice_ids: nextIds,
        initial_amount: nextTotal > 0 ? nextTotal.toFixed(2) : '',
      };
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {mode === 'edit' ? 'Editar obligacion Abasto' : 'Nueva obligacion Abasto'}
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5 }}>
        <Grid container spacing={1.25}>
          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth>
              <InputLabel>Proveedor Abasto</InputLabel>
              <Select value={form.provider_id} label="Proveedor Abasto" onChange={handleProviderChange}>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.companyName || supplier.legalName || `Proveedor ${supplier.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6}>
            <FormControl size="small" fullWidth disabled={!form.provider_id || loadingOptions}>
              <InputLabel>Orden Abasto</InputLabel>
              <Select
                value={form.bank_requisition_id}
                label="Orden Abasto"
                onChange={handleBankRequisitionChange}
                renderValue={(value) => {
                  const selected = bankRequisitions.find(
                    (item) => Number(item.id) === Number(value)
                  );
                  if (!selected) return 'Seleccione orden';
                  return `${selected.purchase_order_code || `Orden ${selected.purchase_order}`} · ${selected.requisition_number || 'Sin requisición'}`;
                }}
              >
                <MenuItem value="">
                  <em>Seleccione orden</em>
                </MenuItem>
                {bankRequisitions.map((item) => (
                  <MenuItem key={item.id} value={item.id} sx={{ py: 0.75 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center" useFlexGap flexWrap="wrap">
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {item.purchase_order_code || `Orden ${item.purchase_order}`}
                        </Typography>
                        <Typography variant="caption">
                          {item.requisition_number || 'Sin requisicion'}
                        </Typography>
                        {item.is_order_expired ? (
                          <Chip size="small" color="warning" variant="outlined" label="Orden vencida" />
                        ) : null}
                        {item.is_monthly_close ? (
                          <Chip size="small" color="error" variant="outlined" label="Cierre mensual" />
                        ) : null}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(item.requisition_date)} · {item.available_invoice_count || 0} facturas · pendiente {formatCurrency(item.available_balance)}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {selectedRequisition && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={2.4}>
                    <DetailField label="Requisicion" value={selectedRequisition.requisition_number || '-'} />
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <DetailField label="Monto requisicion" value={formatCurrency(selectedRequisition.requested_amount)} />
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <DetailField label="Total facturado" value={formatCurrency(selectedRequisition.total_invoiced)} />
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <DetailField label="Ya obligado" value={formatCurrency(selectedRequisition.total_obligated)} />
                  </Grid>
                  <Grid item xs={6} sm={2.4}>
                    <DetailField label="Saldo disponible" value={formatCurrency(selectedRequisition.available_balance)} strong />
                  </Grid>
                </Grid>
                {(selectedRequisition.is_order_expired || selectedRequisition.is_monthly_close) ? (
                  <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                    La orden está vencida, pero conserva facturas pendientes de obligación.
                  </Typography>
                ) : null}
              </Paper>
            </Grid>
          )}

          {selectedSupplier && (
            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1, borderRadius: 1, bgcolor: 'rgba(16, 23, 60, 0.03)' }}>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={3}>
                    <DetailField label="Banco" value={selectedSupplier.abasto_bank_name} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <DetailField label="Convenio" value={selectedSupplier.abasto_agreement_name} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <DetailField label="No. convenio" value={selectedSupplier.abasto_agreement_number} />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <DetailField label="Plazo credito" value={`${selectedSupplier.creditPeriod || 0} dias`} />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          )}

          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="Despacho" value={form.dispatch_number} onChange={handleChange('dispatch_number')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" label="No. requisicion" value={form.requisition_number} onChange={handleChange('requisition_number')} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="date" label="Fecha despacho" value={form.dispatch_date || ''} onChange={handleChange('dispatch_date')} InputLabelProps={{ shrink: true }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth size="small" type="number" label="Monto obligacion" value={form.initial_amount} onChange={handleChange('initial_amount')} inputProps={{ min: 0, step: '0.01' }} />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Paper variant="outlined" sx={{ p: 1, borderRadius: 1, height: '100%' }}>
              <DetailField label="Fin de gracia" value={graceEndDate ? formatDate(graceEndDate) : 'Seleccione fecha despacho'} strong />
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Facturas conciliadas
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Solo facturas Abasto conciliadas con la Orden Abasto seleccionada.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                  <Chip size="small" label={`${selectedInvoices.length} seleccionadas`} variant="outlined" />
                  <Chip size="small" label={formatCurrency(selectedTotal)} color="info" variant="outlined" />
                </Stack>
              </Stack>

              {!hasOrderSelected ? (
                <Typography variant="body2" color="text.secondary">
                  Seleccione una Orden Abasto para ver facturas conciliadas.
                </Typography>
              ) : loadingOptions ? (
                <Typography variant="body2" color="text.secondary">
                  Cargando facturas conciliadas...
                </Typography>
              ) : !eligibleInvoices.length ? (
                <Typography variant="body2" color="text.secondary">
                  Esta orden no tiene facturas Abasto conciliadas disponibles.
                </Typography>
              ) : (
                <Stack spacing={0.75} sx={{ maxHeight: 220, overflowY: 'auto', pr: 0.5 }}>
                  {eligibleInvoices.map((invoice) => {
                    const checked = form.invoice_ids.map(Number).includes(Number(invoice.id));
                    const canSelect = invoice.can_select_for_obligation
                      ?? toNumber(invoice.available_balance) > 0;
                    return (
                      <Paper
                        key={invoice.id}
                        variant="outlined"
                        sx={{
                          p: 1,
                          borderRadius: 1,
                          borderColor: checked ? 'primary.main' : 'divider',
                          bgcolor: checked ? 'rgba(16, 23, 60, 0.04)' : 'background.paper',
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Checkbox
                            size="small"
                            checked={checked}
                            onChange={() => toggleInvoice(invoice.id)}
                            disabled={!canSelect}
                            sx={{ p: 0.5 }}
                          />
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={0.75} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                {invoiceLabel(invoice)}
                              </Typography>
                              <Chip
                                size="small"
                                label={bankPaymentStatusLabels[invoice.bank_payment_status] || invoice.payment_status || 'Sin estado'}
                                variant="outlined"
                              />
                              {!canSelect && (
                                <Chip
                                  size="small"
                                  color="default"
                                  label="Sin saldo disponible"
                                  variant="outlined"
                                />
                              )}
                            </Stack>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(invoice.date)} - Bruto {formatCurrency(invoice.gross_amount ?? invoice.total)}
                              {` - NC ${formatCurrency(invoice.confirmed_credit_notes)}`}
                              {` - Esperado ${formatCurrency(invoice.pending_expected_credit)}`}
                              {Math.abs(toNumber(invoice.linked_credit_difference)) > 0.009 && ` - Diferencia NC ${formatCurrency(invoice.linked_credit_difference)}`}
                              {` - Neto Abasto ${formatCurrency(invoice.abasto_net_amount ?? invoice.available_balance)}`}
                              {` - Disponible ${formatCurrency(invoice.available_balance ?? invoice.saldo)}`}
                            </Typography>
                          </Box>
                          <Stack spacing={0.5} alignItems="flex-end">
                            <Chip
                              size="small"
                              label={invoice.status || 'Sin ajustes'}
                              variant="outlined"
                              color={Math.abs(toNumber(invoice.linked_credit_difference)) > 0.009 ? 'error' : toNumber(invoice.pending_expected_credit) > 0 ? 'warning' : toNumber(invoice.confirmed_credit_notes) > 0 ? 'success' : 'default'}
                            />
                            <Stack direction="row" spacing={0.25} useFlexGap flexWrap="wrap" justifyContent="flex-end">
                              <Button size="small" variant="text" onClick={() => onAddExpectedCredit?.(invoice)}>
                                + Esperado
                              </Button>
                              <Button size="small" variant="text" onClick={() => onAddRealCreditNote?.(invoice)}>
                                + Nota de crédito
                              </Button>
                              <Button size="small" variant="outlined" onClick={() => onManageCredits?.(invoice)}>
                                Ver créditos ({Number(invoice.credit_items_count || 0)})
                              </Button>
                            </Stack>
                          </Stack>
                          {invoice.document_url && (
                            <Tooltip title="Ver documento">
                              <IconButton size="small" component="a" href={invoice.document_url} target="_blank" rel="noreferrer">
                                <InsertDriveFile fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </Paper>
                    );
                  })}
                </Stack>
              )}

              <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={1}>
                  <Grid item xs={6} sm={3}>
                    <DetailField label="Seleccionadas" value={selectedInvoices.length} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <DetailField label="Total seleccionado" value={formatCurrency(selectedTotal)} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <DetailField label="Monto obligacion" value={formatCurrency(form.initial_amount)} />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <DetailField label="Saldo orden" value={formatCurrency(orderRemaining)} strong />
                  </Grid>
                </Grid>
                {Math.abs(amountDifference) > 0.009 && selectedInvoices.length > 0 && (
                  <Typography variant="caption" color="warning.main">
                    El total seleccionado no coincide con el monto de la obligacion.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12}>
            <TextField fullWidth multiline minRows={2} size="small" label="Notas" value={form.notes} onChange={handleChange('notes')} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onSave}
          disabled={saving || !form.provider_id || !form.purchase_order_id || !form.dispatch_number || !form.dispatch_date || !form.initial_amount || !form.invoice_ids.length}
        >
          {saving ? 'Guardando' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function PaymentDialog({ open, obligation, form, setForm, file, setFile, onClose, onSave, saving }) {
  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };
  const paymentTotal = toNumber(form.capital_amount) + toNumber(form.interest_amount) + toNumber(form.late_fee_amount);
  const balance = toNumber(obligation?.balance);
  const exceedsBalance = paymentTotal > balance && paymentTotal > 0;
  const noAmount = paymentTotal <= 0;
  const blocked = !canRegisterPayment(obligation);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Registrar abono
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5 }}>
        <Stack spacing={1.25}>
          <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <DetailField label="Despacho" value={obligation?.dispatch_number} />
              </Grid>
              <Grid item xs={6}>
                <DetailField label="Saldo actual" value={formatCurrency(obligation?.balance)} strong />
              </Grid>
            </Grid>
          </Paper>

          <Grid container spacing={1.25}>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="date" label="Fecha" value={form.payment_date || ''} onChange={handleChange('payment_date')} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" type="number" label="Capital" value={form.capital_amount} onChange={handleChange('capital_amount')} inputProps={{ min: 0, step: '0.01' }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="number" label="Interes" value={form.interest_amount} onChange={handleChange('interest_amount')} inputProps={{ min: 0, step: '0.01' }} />
            </Grid>
            <Grid item xs={6} sm={2}>
              <TextField fullWidth size="small" type="number" label="Mora" value={form.late_fee_amount} onChange={handleChange('late_fee_amount')} inputProps={{ min: 0, step: '0.01' }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Referencia" value={form.reference} onChange={handleChange('reference')} />
            </Grid>
            <Grid item xs={12}>
              <Button component="label" variant="outlined" size="small" startIcon={<AttachFile />} sx={{ textTransform: 'none' }}>
                {file ? file.name : 'Documento opcional'}
                <input hidden type="file" accept="image/*,application/pdf" onChange={(event) => setFile(event.target.files?.[0] || null)} />
              </Button>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} size="small" label="Notas" value={form.notes} onChange={handleChange('notes')} />
            </Grid>
            {exceedsBalance && (
              <Grid item xs={12}>
                <Typography variant="caption" color="error">
                  El abono no puede ser mayor al saldo actual.
                </Typography>
              </Grid>
            )}
            {blocked && (
              <Grid item xs={12}>
                <Typography variant="caption" color="error">
                  No se puede registrar abono en obligaciones pagadas o anuladas.
                </Typography>
              </Grid>
            )}
          </Grid>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button size="small" variant="contained" onClick={onSave} disabled={saving || blocked || !form.payment_date || noAmount || exceedsBalance}>
          {saving ? 'Guardando' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreditItemFormDialog({
  open,
  invoice,
  invoices,
  itemType,
  editing,
  form,
  setForm,
  onClose,
  onSave,
  saving,
}) {
  const handleChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };
  const isExpected = itemType === 'EXPECTED';
  const invoiceOptions = Array.from(
    new Map(
      [invoice, ...(invoices || [])]
        .filter(Boolean)
        .map((candidate) => [Number(candidate.id), candidate])
    ).values()
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {isExpected
          ? `${editing ? 'Editar' : 'Nuevo'} descuento esperado`
          : `${editing ? 'Corregir' : 'Registrar'} nota de crédito recibida`}
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5 }}>
        <Stack spacing={1.25}>
          <Paper variant="outlined" sx={{ p: 1, borderRadius: 1 }}>
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <DetailField label="Factura" value={invoice ? `${invoice.series || ''}-${invoice.number || invoice.id}`.replace(/^-/, '') : '-'} />
              </Grid>
              <Grid item xs={6}>
                <DetailField label="Neto actual" value={formatCurrency(invoice?.abasto_net_amount ?? invoice?.available_balance)} strong />
              </Grid>
            </Grid>
          </Paper>
          {isExpected && editing && (
            <FormControl size="small" fullWidth>
              <InputLabel>Factura</InputLabel>
              <Select
                value={form.invoice_id}
                label="Factura"
                onChange={handleChange('invoice_id')}
              >
                {invoiceOptions.map((candidate) => (
                  <MenuItem key={candidate.id} value={candidate.id}>
                    {`${candidate.series || ''}-${candidate.number || candidate.id}`.replace(/^-/, '')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {!isExpected && (
            <>
              <TextField
                size="small"
                label="Número de NC"
                value={form.number}
                onChange={handleChange('number')}
                fullWidth
              />
              <TextField
                size="small"
                type="date"
                label="Fecha"
                value={form.date}
                onChange={handleChange('date')}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </>
          )}
          <TextField
            size="small"
            type="number"
            label="Monto"
            value={form.amount}
            onChange={handleChange('amount')}
            inputProps={{ min: 0, step: '0.01' }}
            fullWidth
          />
          {isExpected && (
            <TextField
              size="small"
              label="Número esperado (opcional)"
              value={form.expected_note_number}
              onChange={handleChange('expected_note_number')}
              fullWidth
            />
          )}
          <TextField
            size="small"
            label={isExpected ? 'Observación' : 'Motivo / observación'}
            value={form.reason}
            onChange={handleChange('reason')}
            multiline={!isExpected}
            minRows={isExpected ? 1 : 2}
            fullWidth
          />
          {isExpected && (
            <TextField
              size="small"
              label="Comentario"
              value={form.notes}
              onChange={handleChange('notes')}
              multiline
              minRows={2}
              fullWidth
            />
          )}
          {!isExpected && (
            <>
              <Button variant="outlined" component="label" startIcon={<AttachFile />}>
                {form.file?.name || (editing ? 'Reemplazar archivo' : 'Adjuntar archivo')}
                <input
                  hidden
                  type="file"
                  onChange={(event) => setForm((prev) => ({
                    ...prev,
                    file: event.target.files?.[0] || null,
                  }))}
                />
              </Button>
              {editing && (
                <TextField
                  size="small"
                  label="Motivo de corrección"
                  value={form.correction_reason}
                  onChange={handleChange('correction_reason')}
                  fullWidth
                />
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onSave}
          disabled={
            saving
            || !invoice?.id
            || !form.amount
            || (!isExpected && (!form.number || !form.date))
          }
        >
          {saving ? 'Guardando' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CreditItemsDialog({
  open,
  invoice,
  items,
  loading,
  onClose,
  onAddExpected,
  onAddCreditNote,
  onEdit,
  onDelete,
  onCancel,
}) {
  const rows = useMemo(
    () => (items || []).map((item) => ({
      ...item,
      rowKey: `${item.item_type}-${item.id}-${item.allocation_id || 0}`,
    })),
    [items]
  );
  const columns = useMemo(() => [
    {
      field: 'item_type',
      headerName: 'Tipo',
      width: 125,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          color={row.item_type === 'EXPECTED' ? 'warning' : 'success'}
          variant="outlined"
          label={row.item_type === 'EXPECTED' ? 'Esperado' : 'Nota de crédito'}
        />
      ),
    },
    { field: 'number', headerName: 'Número', minWidth: 135, flex: 1 },
    {
      field: 'date',
      headerName: 'Fecha',
      width: 115,
      valueFormatter: (value) => formatDate(value),
    },
    {
      field: 'amount',
      headerName: 'Monto',
      width: 125,
      align: 'right',
      headerAlign: 'right',
      valueFormatter: (value) => formatCurrency(value),
    },
    {
      field: 'status_label',
      headerName: 'Estado',
      width: 145,
      renderCell: ({ row }) => (
        <Chip
          size="small"
          label={row.status_label}
          color={
            row.status === 'PENDING_NOTE'
              ? 'warning'
              : row.status === 'CANCELLED'
                ? 'default'
                : 'success'
          }
          variant="outlined"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 140,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.25}>
          {row.can_edit && (
            <Tooltip title="Editar">
              <IconButton size="small" onClick={() => onEdit(row)}>
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {row.can_delete && (
            <Tooltip title="Eliminar esperado">
              <IconButton size="small" color="error" onClick={() => onDelete(row)}>
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {row.can_cancel && (
            <Tooltip title="Anular nota de crédito">
              <IconButton size="small" color="error" onClick={() => onCancel(row)}>
                <Block fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {row.file_url && (
            <Tooltip title="Ver archivo">
              <IconButton
                size="small"
                component="a"
                href={row.file_url}
                target="_blank"
                rel="noreferrer"
              >
                <InsertDriveFile fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      ),
    },
  ], [onCancel, onDelete, onEdit]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Créditos Abasto · {invoice
          ? `${invoice.series || ''}-${invoice.number || invoice.id}`.replace(/^-/, '')
          : '-'}
      </DialogTitle>
      <DialogContent>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mb: 1.25, mt: 0.5 }}
        >
          <Button variant="outlined" onClick={onAddExpected}>
            Descuento esperado
          </Button>
          <Button variant="contained" onClick={onAddCreditNote}>
            Nota de crédito recibida
          </Button>
        </Stack>
        <Box sx={{ height: 330 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.rowKey}
            loading={loading}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25]}
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            localeText={{ noRowsLabel: 'No hay descuentos ni notas para esta factura' }}
            density="compact"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function TimelineIcon({ type }) {
  const iconProps = { fontSize: 'small' };
  if (type === 'PAYMENT') return <Payments {...iconProps} />;
  if (type === 'INVOICE') return <ReceiptLong {...iconProps} />;
  if (type === 'PAID') return <CheckCircle {...iconProps} />;
  if (type === 'OBLIGATION') return <AccountBalance {...iconProps} />;
  return <History {...iconProps} />;
}

function DetailDialog({ open, obligation, onClose }) {
  const invoices = obligation?.invoices || [];
  const payments = obligation?.payments || [];
  const documents = obligation?.documents || [];
  const timeline = obligation?.timeline || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {!obligation ? 'Detalle Abasto' : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {obligation.dispatch_number || 'Detalle Abasto'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {obligation.provider_name || '-'} - Saldo {formatCurrency(obligation.balance)}
              </Typography>
            </Box>
            <StatusChip value={obligation.internal_status} />
          </Stack>
        )}
      </DialogTitle>
      <DialogContent sx={{ pt: 1.5 }}>
        {!obligation ? (
          <Typography variant="body2">Sin informacion.</Typography>
        ) : (
          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
              <Grid container spacing={1.25}>
                <Grid item xs={12} sm={3}>
                  <DetailField label="No. requisicion" value={obligation.requisition_number} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <DetailField label="Fecha despacho" value={formatDate(obligation.dispatch_date)} />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <DetailField label="Fin gracia" value={formatDate(obligation.grace_end_date)} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <DetailField label="Monto inicial" value={formatCurrency(obligation.initial_amount)} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <DetailField label="Pagado" value={formatCurrency(obligation.paid_amount)} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <DetailField label="Saldo actual" value={formatCurrency(obligation.balance)} strong />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <DetailField label="Interes" value={formatCurrency(obligation.interest)} />
                </Grid>
                <Grid item xs={6} sm={2}>
                  <DetailField label="Mora" value={formatCurrency(obligation.late_fee)} />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <DetailField label="Orden" value={obligation.purchase_order_code} />
                </Grid>
                {obligation.notes && (
                  <Grid item xs={12}>
                    <DetailField label="Notas" value={obligation.notes} />
                  </Grid>
                )}
              </Grid>
            </Paper>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                Facturas vinculadas
              </Typography>
              {!invoices.length ? (
                <Typography variant="body2" color="text.secondary">Sin facturas asociadas.</Typography>
              ) : (
                invoices.map((item) => (
                  <Paper key={item.id} variant="outlined" sx={{ p: 1, mb: 0.75, borderRadius: 1 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.invoice_series || ''}-{item.invoice_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(item.invoice_date)} - {bankPaymentStatusLabels[item.bank_payment_status] || item.payment_status || 'Sin estado'}
                        </Typography>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(item.invoice_total)}</Typography>
                        {item.document_url && (
                          <IconButton size="small" component="a" href={item.document_url} target="_blank" rel="noreferrer">
                            <InsertDriveFile fontSize="small" />
                          </IconButton>
                        )}
                      </Stack>
                    </Stack>
                  </Paper>
                ))
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                Abonos realizados
              </Typography>
              {!payments.length ? (
                <Typography variant="body2" color="text.secondary">Sin abonos registrados.</Typography>
              ) : (
                payments.map((payment) => (
                  <Paper key={payment.id} variant="outlined" sx={{ p: 1, mb: 0.75, borderRadius: 1 }}>
                    <Grid container spacing={1} alignItems="center">
                      <Grid item xs={12} sm={2}>
                        <DetailField label="Fecha" value={formatDate(payment.payment_date)} />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <DetailField label="Capital" value={formatCurrency(payment.capital_amount)} />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <DetailField label="Interes" value={formatCurrency(payment.interest_amount)} />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <DetailField label="Mora" value={formatCurrency(payment.late_fee_amount)} />
                      </Grid>
                      <Grid item xs={6} sm={2}>
                        <DetailField label="Total" value={formatCurrency(payment.total_amount)} strong />
                      </Grid>
                      <Grid item xs={12} sm={2}>
                        {payment.document_url && (
                          <Button size="small" href={payment.document_url} target="_blank" rel="noreferrer" sx={{ textTransform: 'none' }}>
                            Documento
                          </Button>
                        )}
                      </Grid>
                    </Grid>
                  </Paper>
                ))
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                Documentos
              </Typography>
              {!documents.length ? (
                <Typography variant="body2" color="text.secondary">Sin documentos asociados.</Typography>
              ) : (
                documents.map((document, index) => (
                  <Button
                    key={`${document.type}-${index}`}
                    size="small"
                    variant="outlined"
                    startIcon={<InsertDriveFile />}
                    href={document.url}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ mr: 1, mb: 1, textTransform: 'none' }}
                  >
                    {document.label || 'Documento'}
                  </Button>
                ))
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                Timeline
              </Typography>
              {!timeline.length ? (
                <Typography variant="body2" color="text.secondary">Sin eventos registrados.</Typography>
              ) : (
                <Stack spacing={0.75}>
                  {timeline.map((event, index) => (
                    <Stack key={`${event.type}-${index}`} direction="row" spacing={1} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: event.type === 'PAID' ? 'success.light' : 'rgba(16, 23, 60, 0.08)',
                          color: event.type === 'PAID' ? 'success.dark' : '#10173c',
                          flexShrink: 0,
                        }}
                      >
                        <TimelineIcon type={event.type} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {event.label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(event.date)} {event.description ? `- ${event.description}` : ''}
                          {event.amount !== undefined && event.amount !== null ? ` - ${formatCurrency(event.amount)}` : ''}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AbastoObligations() {
  const dispatch = useDispatch();
  const [suppliers, setSuppliers] = useState([]);
  const [obligations, setObligations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    provider: '',
    status: '',
    startDate: '',
    endDate: '',
    requisition: '',
    dispatch: '',
    search: '',
  });
  const [bankRequisitions, setBankRequisitions] = useState([]);
  const [eligibleInvoices, setEligibleInvoices] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [obligationDialogOpen, setObligationDialogOpen] = useState(false);
  const [obligationMode, setObligationMode] = useState('create');
  const [obligationForm, setObligationForm] = useState(emptyObligationForm);
  const [savingObligation, setSavingObligation] = useState(false);
  const [selectedObligation, setSelectedObligation] = useState(null);
  const [optionsReloadKey, setOptionsReloadKey] = useState(0);
  const [detailOpen, setDetailOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [paymentForm, setPaymentForm] = useState(emptyPaymentForm);
  const [paymentFile, setPaymentFile] = useState(null);
  const [savingPayment, setSavingPayment] = useState(false);
  const [creditItemsOpen, setCreditItemsOpen] = useState(false);
  const [creditItemsInvoice, setCreditItemsInvoice] = useState(null);
  const [creditItems, setCreditItems] = useState([]);
  const [loadingCreditItems, setLoadingCreditItems] = useState(false);
  const [creditItemFormOpen, setCreditItemFormOpen] = useState(false);
  const [creditItemType, setCreditItemType] = useState('EXPECTED');
  const [editingCreditItem, setEditingCreditItem] = useState(null);
  const [creditItemForm, setCreditItemForm] = useState(emptyCreditItemForm);
  const [savingCreditItem, setSavingCreditItem] = useState(false);
  const [creditActionDialog, setCreditActionDialog] = useState({
    open: false,
    action: '',
    item: null,
    reason: '',
  });
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    dispatch(addTab({ label: 'Abasto', path: '/abasto' }));
  }, [dispatch]);

  const notify = useCallback((message, severity = 'info') => {
    setSnack({ open: true, message, severity });
  }, []);

  const fetchSuppliers = useCallback(async () => {
    try {
      const { data } = await API.get('suppliers/minimal/');
      const items = Array.isArray(data) ? data : data?.results || [];
      setSuppliers(items.filter((supplier) => supplier.payment_flow_type === BANK_REQUISITION_FLOW));
    } catch (error) {
      console.error('Error cargando proveedores Abasto', error);
      setSuppliers([]);
    }
  }, []);

  const fetchObligations = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        provider: filters.provider || undefined,
        status: filters.status || undefined,
        start_date: filters.startDate || undefined,
        end_date: filters.endDate || undefined,
        requisition: filters.requisition || undefined,
        dispatch: filters.dispatch || undefined,
        search: filters.search || undefined,
      };
      const { data } = await API.get('abasto-obligations/', { params });
      const items = Array.isArray(data) ? data : data?.results || [];
      setObligations(items);
    } catch (error) {
      console.error('Error cargando obligaciones Abasto', error);
      setObligations([]);
      notify('No se pudieron cargar obligaciones Abasto', 'error');
    } finally {
      setLoading(false);
    }
  }, [filters, notify]);

  const summary = useMemo(() => (
    obligations.reduce((acc, item) => {
      const balance = toNumber(item.balance);
      const interestLateFee = toNumber(item.interest) + toNumber(item.late_fee);
      const graceDays = daysUntil(item.grace_end_date);
      acc.total += 1;
      acc.pendingBalance += Math.max(balance, 0);
      acc.interestLateFee += interestLateFee;
      if (String(item.internal_status) === 'OVERDUE' || (balance > 0 && graceDays !== null && graceDays < 0)) {
        acc.overdue += 1;
      }
      if (balance > 0 && graceDays !== null && graceDays >= 0 && graceDays <= ALERT_WINDOW_DAYS) {
        acc.upcoming += 1;
      }
      (item.payments || []).forEach((payment) => {
        if (isCurrentMonthDate(payment.payment_date)) {
          acc.paidThisMonth += toNumber(payment.total_amount);
        }
      });
      return acc;
    }, { total: 0, pendingBalance: 0, overdue: 0, upcoming: 0, paidThisMonth: 0, interestLateFee: 0 })
  ), [obligations]);

  const exportCsv = useCallback(() => {
    const headers = [
      'proveedor',
      'orden',
      'requisicion',
      'despacho',
      'fecha despacho',
      'fin gracia',
      'monto inicial',
      'pagado',
      'saldo',
      'interes',
      'mora',
      'estado',
      'facturas asociadas',
    ];
    const rows = obligations.map((item) => [
      item.provider_name || '',
      item.purchase_order_code || '',
      item.requisition_number || '',
      item.dispatch_number || '',
      item.dispatch_date || '',
      item.grace_end_date || '',
      item.initial_amount || '0',
      item.paid_amount || '0',
      item.balance || '0',
      item.interest || '0',
      item.late_fee || '0',
      statusLabels[item.internal_status] || item.internal_status || '',
      (item.linked_invoice_numbers?.length
        ? item.linked_invoice_numbers
        : (item.invoices || []).map((invoice) => `${invoice.invoice_series || ''}-${invoice.invoice_number}`)
      ).filter(Boolean).join('; '),
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map(csvCell).join(','))
      .join('\r\n');
    const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `abasto-obligaciones-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [obligations]);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  useEffect(() => {
    fetchObligations();
  }, [fetchObligations]);

  useEffect(() => {
    const providerId = obligationForm.provider_id;
    if (!providerId || !obligationDialogOpen) {
      setBankRequisitions([]);
      setEligibleInvoices([]);
      return;
    }

    let mounted = true;
    setLoadingOptions(true);
    Promise.all([
      API.get('abasto-obligations/eligible_orders/', {
        params: {
          provider: providerId,
          current_obligation_id: selectedObligation?.id || undefined,
        },
      }),
      API.get('abasto-obligations/eligible_invoices/', {
        params: {
          provider: providerId,
          bank_requisition: obligationForm.bank_requisition_id || undefined,
          purchase_order: obligationForm.purchase_order_id || undefined,
          current_obligation_id: selectedObligation?.id || undefined,
        },
      }),
    ])
      .then(([reqRes, invRes]) => {
        if (!mounted) return;
        setBankRequisitions(Array.isArray(reqRes.data) ? reqRes.data : reqRes.data?.results || []);
        const invoiceOptions = Array.isArray(invRes.data) ? invRes.data : invRes.data?.results || [];
        setEligibleInvoices(invoiceOptions);
        setObligationForm((prev) => {
          if (!prev.invoice_ids?.length) return prev;
          const map = new Map(invoiceOptions.map((invoice) => [Number(invoice.id), invoice]));
          const selectableIds = prev.invoice_ids.filter((id) => {
            const invoice = map.get(Number(id));
            return Boolean(
              invoice
              && (
                invoice.can_select_for_obligation
                ?? toNumber(invoice.available_balance) > 0
              )
            );
          });
          const total = selectableIds.reduce((sum, id) => {
            const invoice = map.get(Number(id));
            return sum + toNumber(invoice?.abasto_net_amount ?? invoice?.available_balance ?? invoice?.total);
          }, 0);
          return {
            ...prev,
            invoice_ids: selectableIds,
            initial_amount: total > 0 ? total.toFixed(2) : '',
          };
        });
      })
      .catch((error) => {
        console.error('Error cargando opciones Abasto', error);
        if (mounted) {
          setBankRequisitions([]);
          setEligibleInvoices([]);
        }
      })
      .finally(() => {
        if (mounted) setLoadingOptions(false);
      });

    return () => {
      mounted = false;
    };
  }, [obligationDialogOpen, obligationForm.provider_id, obligationForm.bank_requisition_id, obligationForm.purchase_order_id, selectedObligation?.id, optionsReloadKey]);

  const openCreateDialog = () => {
    setSelectedObligation(null);
    setObligationMode('create');
    setObligationForm(emptyObligationForm);
    setObligationDialogOpen(true);
  };

  const openEditDialog = (row) => {
    setSelectedObligation(row);
    setObligationMode('edit');
    setObligationForm({
      provider_id: row.provider?.id || row.provider || '',
      purchase_order_id: row.purchase_order || '',
      bank_requisition_id: row.bank_requisition || '',
      dispatch_number: row.dispatch_number || '',
      requisition_number: row.requisition_number || '',
      dispatch_date: row.dispatch_date || '',
      initial_amount: row.initial_amount || '',
      notes: row.notes || '',
      invoice_ids: (row.invoices || []).map((invoice) => invoice.invoice_id),
    });
    setObligationDialogOpen(true);
  };

  const openDetailDialog = async (row) => {
    try {
      const { data } = await API.get(`abasto-obligations/${row.id}/`);
      setSelectedObligation(data);
      setDetailOpen(true);
    } catch (error) {
      console.error('Error cargando detalle Abasto', error);
      notify('No se pudo cargar el detalle Abasto', 'error');
    }
  };

  const openPaymentDialog = async (row) => {
    if (!canRegisterPayment(row)) {
      notify('No se puede registrar abono en obligaciones pagadas o anuladas', 'warning');
      return;
    }
    try {
      const { data } = await API.get(`abasto-obligations/${row.id}/`);
      if (!canRegisterPayment(data)) {
        notify('No se puede registrar abono en obligaciones pagadas o anuladas', 'warning');
        return;
      }
      setSelectedObligation(data);
      setPaymentForm(emptyPaymentForm);
      setPaymentFile(null);
      setPaymentOpen(true);
    } catch (error) {
      console.error('Error cargando obligacion Abasto', error);
      notify('No se pudo cargar la obligacion Abasto', 'error');
    }
  };

  const fetchCreditItems = async (invoiceId) => {
    if (!invoiceId) return;
    try {
      setLoadingCreditItems(true);
      const { data } = await API.get('order/abasto-credit-items/', {
        params: { invoice_id: invoiceId },
      });
      setCreditItems(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error cargando créditos Abasto', error);
      setCreditItems([]);
      notify(extractApiError(error, 'No se pudieron cargar los créditos Abasto'), 'error');
    } finally {
      setLoadingCreditItems(false);
    }
  };

  const openCreditItemsDialog = async (invoice) => {
    setCreditItemsInvoice(invoice);
    setCreditItemsOpen(true);
    await fetchCreditItems(invoice.id);
  };

  const openCreditItemForm = (itemType, item = null, invoiceOverride = null) => {
    const targetInvoice = invoiceOverride || creditItemsInvoice;
    if (invoiceOverride) {
      setCreditItemsInvoice(invoiceOverride);
    }
    setCreditItemType(itemType);
    setEditingCreditItem(item);
    setCreditItemForm({
      ...emptyCreditItemForm,
      invoice_id: item?.invoice_id || targetInvoice?.id || '',
      item_type: itemType,
      amount: item?.amount || '',
      number: item?.number || '',
      date: item?.date || emptyCreditItemForm.date,
      reason: item?.reason || '',
      expected_note_number: itemType === 'EXPECTED' ? (item?.number || '') : '',
      notes: item?.notes || '',
      correction_reason: '',
      file: null,
    });
    setCreditItemFormOpen(true);
  };

  const saveCreditItem = async () => {
    if (!creditItemsInvoice?.id || toNumber(creditItemForm.amount) <= 0) {
      notify('Ingresa un monto válido', 'warning');
      return;
    }
    try {
      setSavingCreditItem(true);
      if (creditItemType === 'EXPECTED') {
        const payload = {
          invoice_id: Number(creditItemForm.invoice_id || creditItemsInvoice.id),
          amount: toMoney(creditItemForm.amount),
          expected_note_number: creditItemForm.expected_note_number || '',
          reason: creditItemForm.reason || 'Descuento esperado Abasto',
          notes: creditItemForm.notes || '',
        };
        if (editingCreditItem) {
          await API.patch(
            `order/abasto-credit-items/expected/${editingCreditItem.id}/`,
            payload
          );
        } else {
          await API.post('order/abasto-credit-items/expected/', payload);
        }
      } else {
        const formData = new FormData();
        formData.append('invoice_id', String(creditItemsInvoice.id));
        formData.append('number', creditItemForm.number || '');
        formData.append('date', creditItemForm.date || '');
        formData.append('amount', toMoney(creditItemForm.amount));
        formData.append('reason', creditItemForm.reason || '');
        if (creditItemForm.correction_reason) {
          formData.append('correction_reason', creditItemForm.correction_reason);
        }
        if (creditItemForm.file) formData.append('file', creditItemForm.file);
        const config = { headers: { 'Content-Type': 'multipart/form-data' } };
        if (editingCreditItem) {
          await API.patch(
            `order/abasto-credit-items/credit-note/${editingCreditItem.id}/`,
            formData,
            config
          );
        } else {
          await API.post(
            'order/abasto-credit-items/credit-note/',
            formData,
            config
          );
        }
      }
      notify(
        creditItemType === 'EXPECTED'
          ? 'Descuento esperado actualizado'
          : 'Nota de crédito actualizada',
        'success'
      );
      setCreditItemFormOpen(false);
      await fetchCreditItems(creditItemsInvoice.id);
      setOptionsReloadKey((value) => value + 1);
      fetchObligations();
    } catch (error) {
      console.error('Error guardando crédito Abasto', error);
      notify(extractApiError(error, 'No se pudo guardar el crédito Abasto'), 'error');
    } finally {
      setSavingCreditItem(false);
    }
  };

  const confirmCreditAction = async () => {
    const { action, item, reason } = creditActionDialog;
    if (!item) return;
    if (action === 'cancel' && !String(reason || '').trim()) {
      notify('Indica el motivo de anulación', 'warning');
      return;
    }
    try {
      setSavingCreditItem(true);
      if (action === 'delete') {
        await API.delete(`order/abasto-credit-items/expected/${item.id}/`);
      } else {
        await API.post(
          `order/abasto-credit-items/credit-note/${item.id}/cancel/`,
          { cancel_reason: reason }
        );
      }
      setCreditActionDialog({ open: false, action: '', item: null, reason: '' });
      notify(
        action === 'delete'
          ? 'Descuento esperado eliminado'
          : 'Nota de crédito anulada',
        'success'
      );
      await fetchCreditItems(creditItemsInvoice.id);
      setOptionsReloadKey((value) => value + 1);
      fetchObligations();
    } catch (error) {
      console.error('Error actualizando crédito Abasto', error);
      notify(extractApiError(error, 'No se pudo completar la acción'), 'error');
    } finally {
      setSavingCreditItem(false);
    }
  };

  const saveObligation = async () => {
    try {
      setSavingObligation(true);
      const payload = {
        ...obligationForm,
        provider_id: obligationForm.provider_id,
        purchase_order_id: toNullable(obligationForm.purchase_order_id),
        bank_requisition_id: toNullable(obligationForm.bank_requisition_id),
        dispatch_date: toNullable(obligationForm.dispatch_date),
        initial_amount: toMoney(obligationForm.initial_amount),
        invoice_ids: obligationForm.invoice_ids,
      };
      if (obligationMode === 'edit' && selectedObligation?.id) {
        await API.patch(`abasto-obligations/${selectedObligation.id}/`, payload);
      } else {
        await API.post('abasto-obligations/', payload);
      }
      notify('Obligacion Abasto guardada', 'success');
      setObligationDialogOpen(false);
      await fetchObligations();
    } catch (error) {
      console.error('Error guardando obligacion Abasto', error);
      notify(extractApiError(error, 'No se pudo guardar la obligacion Abasto'), 'error');
    } finally {
      setSavingObligation(false);
    }
  };

  const savePayment = async () => {
    if (!selectedObligation?.id) return;
    if (!canRegisterPayment(selectedObligation)) {
      notify('No se puede registrar abono en obligaciones pagadas o anuladas', 'warning');
      return;
    }
    const paymentTotal = toNumber(paymentForm.capital_amount) + toNumber(paymentForm.interest_amount) + toNumber(paymentForm.late_fee_amount);
    if (paymentTotal <= 0) {
      notify('Ingresa un monto de abono mayor a cero', 'warning');
      return;
    }
    if (paymentTotal > toNumber(selectedObligation.balance)) {
      notify('El abono no puede ser mayor al saldo actual', 'error');
      return;
    }
    try {
      setSavingPayment(true);
      const fd = new FormData();
      fd.append('payment_date', paymentForm.payment_date);
      fd.append('capital_amount', toMoney(paymentForm.capital_amount));
      fd.append('interest_amount', toMoney(paymentForm.interest_amount));
      fd.append('late_fee_amount', toMoney(paymentForm.late_fee_amount));
      fd.append('reference', paymentForm.reference || '');
      fd.append('notes', paymentForm.notes || '');
      if (paymentFile) fd.append('document', paymentFile);
      await API.post(`abasto-obligations/${selectedObligation.id}/register_payment/`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      notify('Abono Abasto registrado', 'success');
      setPaymentOpen(false);
      await fetchObligations();
    } catch (error) {
      console.error('Error registrando abono Abasto', error);
      notify(extractApiError(error, 'No se pudo registrar el abono'), 'error');
    } finally {
      setSavingPayment(false);
    }
  };

  const columns = [
    { field: 'dispatch_number', headerName: 'Despacho', width: 125 },
    { field: 'requisition_number', headerName: 'No. requisicion', width: 140 },
    { field: 'provider_name', headerName: 'Proveedor', width: 210 },
    {
      field: 'linked_invoice_numbers',
      headerName: 'Facturas',
      width: 190,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => <InvoiceChips row={row} />,
    },
    {
      field: 'dispatch_date',
      headerName: 'Fecha despacho',
      width: 130,
      renderCell: ({ value }) => <Typography variant="body2">{formatDate(value)}</Typography>,
    },
    {
      field: 'grace_end_date',
      headerName: 'Fin gracia',
      width: 120,
      renderCell: ({ value }) => <Typography variant="body2">{formatDate(value)}</Typography>,
    },
    {
      field: 'initial_amount',
      headerName: 'Monto inicial',
      width: 130,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ value }) => <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(value)}</Typography>,
    },
    {
      field: 'paid_amount',
      headerName: 'Pagado',
      width: 120,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ value }) => <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.dark' }}>{formatCurrency(value)}</Typography>,
    },
    {
      field: 'balance',
      headerName: 'Saldo',
      width: 120,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ value }) => <Typography variant="body2" sx={{ fontWeight: 700, color: Number(value || 0) <= 0 ? 'success.dark' : 'primary.main' }}>{formatCurrency(value)}</Typography>,
    },
    {
      field: 'interest',
      headerName: 'Interes',
      width: 105,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ value }) => <Typography variant="body2">{formatCurrency(value)}</Typography>,
    },
    {
      field: 'late_fee',
      headerName: 'Mora',
      width: 105,
      headerAlign: 'right',
      align: 'right',
      renderCell: ({ value }) => <Typography variant="body2">{formatCurrency(value)}</Typography>,
    },
    {
      field: 'internal_status',
      headerName: 'Estado',
      width: 125,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ value }) => <StatusChip value={value} />,
    },
    {
      field: 'alerts',
      headerName: 'Alertas',
      width: 235,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => <AlertChips row={row} />,
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 145,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.25} alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%' }}>
          <Tooltip title="Ver detalle">
            <IconButton size="small" color="primary" onClick={() => openDetailDialog(row)}>
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Registrar abono">
            <IconButton size="small" color="success" onClick={() => openPaymentDialog(row)} disabled={!canRegisterPayment(row)}>
              <Payments fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Editar">
            <IconButton size="small" color="info" onClick={() => openEditDialog(row)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
        <MainText
          title="Abasto"
          subtitle="Control de obligaciones, despachos y abonos bancarios"
          icon={AccountBalance}
        />
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Tooltip title="Exportar CSV">
            <span>
              <IconButton color="info" onClick={exportCsv} disabled={!obligations.length}>
                <Download />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Nueva obligacion">
            <IconButton color="primary" onClick={openCreateDialog}>
              <AddCircle />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refrescar">
            <IconButton onClick={fetchObligations}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, minmax(0, 1fr))' },
          gap: 1,
          mb: 1.5,
        }}
      >
        {[
          ['Saldo pendiente total', formatCurrency(summary.pendingBalance)],
          ['Obligaciones vencidas', summary.overdue],
          ['Proximas a vencer', summary.upcoming],
          ['Pagado este mes', formatCurrency(summary.paidThisMonth)],
          ['Interes/mora acumulada', formatCurrency(summary.interestLateFee)],
        ].map(([label, value]) => (
          <Paper key={label} variant="outlined" sx={{ p: 1.25, borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {label}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {value}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, borderRadius: 1 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1} alignItems={{ xs: 'stretch', lg: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Proveedor</InputLabel>
            <Select value={filters.provider} label="Proveedor" onChange={(event) => setFilters((prev) => ({ ...prev, provider: event.target.value }))}>
              <MenuItem value="">Todos</MenuItem>
              {suppliers.map((supplier) => (
                <MenuItem key={supplier.id} value={supplier.id}>
                  {supplier.companyName || supplier.legalName || `Proveedor ${supplier.id}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Estado</InputLabel>
            <Select value={filters.status} label="Estado" onChange={(event) => setFilters((prev) => ({ ...prev, status: event.target.value }))}>
              <MenuItem value="">Todos</MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField size="small" type="date" label="Desde" value={filters.startDate} onChange={(event) => setFilters((prev) => ({ ...prev, startDate: event.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField size="small" type="date" label="Hasta" value={filters.endDate} onChange={(event) => setFilters((prev) => ({ ...prev, endDate: event.target.value }))} InputLabelProps={{ shrink: true }} />
          <TextField
            size="small"
            label="Requisicion"
            value={filters.requisition}
            onChange={(event) => setFilters((prev) => ({ ...prev, requisition: event.target.value }))}
            sx={{ minWidth: 150 }}
          />
          <TextField
            size="small"
            label="Despacho"
            value={filters.dispatch}
            onChange={(event) => setFilters((prev) => ({ ...prev, dispatch: event.target.value }))}
            sx={{ minWidth: 150 }}
          />
          <TextField
            size="small"
            placeholder="Buscar..."
            value={filters.search}
            onChange={(event) => setFilters((prev) => ({ ...prev, search: event.target.value }))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 220 }}
          />
        </Stack>
      </Paper>

      <Box sx={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={obligations}
          columns={columns}
          loading={loading}
          pageSizeOptions={[10, 20, 50]}
          pagination
          disableColumnFilter
          disableColumnMenu
          rowHeight={52}
          columnHeaderHeight={50}
          sx={{
            '& .MuiDataGrid-columnHeaders': { backgroundColor: '#10173c', color: '#ffffff' },
            '& .MuiDataGrid-columnHeader': { backgroundColor: '#10173c !important' },
            '& .MuiDataGrid-columnHeaderTitle': { color: '#ffffff' },
            '& .MuiDataGrid-iconButtonContainer': { color: '#ffffff !important' },
            '& .MuiDataGrid-menuIcon': { color: '#ffffff !important' },
            '& .MuiDataGrid-columnSeparator': { color: '#ffffff !important' },
            '& .MuiDataGrid-cell': { padding: '0 8px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center' },
            '& .MuiDataGrid-row:hover': { cursor: 'pointer', backgroundColor: 'rgba(16, 23, 60, 0.08)' },
          }}
        />
      </Box>

      <ObligationDialog
        open={obligationDialogOpen}
        mode={obligationMode}
        form={obligationForm}
        setForm={setObligationForm}
        suppliers={suppliers}
        bankRequisitions={bankRequisitions}
        eligibleInvoices={eligibleInvoices}
        loadingOptions={loadingOptions}
        onManageCredits={openCreditItemsDialog}
        onAddExpectedCredit={(invoice) => openCreditItemForm('EXPECTED', null, invoice)}
        onAddRealCreditNote={(invoice) => openCreditItemForm('CREDIT_NOTE', null, invoice)}
        onClose={() => setObligationDialogOpen(false)}
        onSave={saveObligation}
        saving={savingObligation}
      />

      <CreditItemsDialog
        open={creditItemsOpen}
        invoice={creditItemsInvoice}
        items={creditItems}
        loading={loadingCreditItems}
        onClose={() => setCreditItemsOpen(false)}
        onAddExpected={() => openCreditItemForm('EXPECTED')}
        onAddCreditNote={() => openCreditItemForm('CREDIT_NOTE')}
        onEdit={(item) => openCreditItemForm(item.item_type, item)}
        onDelete={(item) => setCreditActionDialog({
          open: true,
          action: 'delete',
          item,
          reason: '',
        })}
        onCancel={(item) => setCreditActionDialog({
          open: true,
          action: 'cancel',
          item,
          reason: '',
        })}
      />

      <CreditItemFormDialog
        open={creditItemFormOpen}
        invoice={creditItemsInvoice}
        invoices={eligibleInvoices}
        itemType={creditItemType}
        editing={Boolean(editingCreditItem)}
        form={creditItemForm}
        setForm={setCreditItemForm}
        onClose={() => setCreditItemFormOpen(false)}
        onSave={saveCreditItem}
        saving={savingCreditItem}
      />

      <Dialog
        open={creditActionDialog.open}
        onClose={() => setCreditActionDialog({
          open: false,
          action: '',
          item: null,
          reason: '',
        })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {creditActionDialog.action === 'delete'
            ? 'Eliminar descuento esperado'
            : 'Anular nota de crédito'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.25 }}>
            {creditActionDialog.action === 'delete'
              ? 'El descuento provisional dejará de reducir el neto Abasto.'
              : 'La NC y su asignación quedarán anuladas, conservando la trazabilidad.'}
          </Typography>
          {creditActionDialog.action === 'cancel' && (
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              label="Motivo de anulación"
              value={creditActionDialog.reason}
              onChange={(event) => setCreditActionDialog((prev) => ({
                ...prev,
                reason: event.target.value,
              }))}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setCreditActionDialog({
              open: false,
              action: '',
              item: null,
              reason: '',
            })}
            disabled={savingCreditItem}
          >
            Volver
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={confirmCreditAction}
            disabled={savingCreditItem}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      <PaymentDialog
        open={paymentOpen}
        obligation={selectedObligation}
        form={paymentForm}
        setForm={setPaymentForm}
        file={paymentFile}
        setFile={setPaymentFile}
        onClose={() => setPaymentOpen(false)}
        onSave={savePayment}
        saving={savingPayment}
      />

      <DetailDialog
        open={detailOpen}
        obligation={selectedObligation}
        onClose={() => setDetailOpen(false)}
      />

      <CustomSnackBar
        open={snack.open}
        setOpen={(open) => setSnack((prev) => ({ ...prev, open }))}
        message={snack.message}
        severity={snack.severity}
      />
    </>
  );
}
